import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, MessageSquare } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../layout/ListFiltr';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZakaznikKarta from './ZakaznikKarta';
import { SECTION_COLOR, SECTION_BG, CERVENA, fmtKc, Nadpis, RadekPanelu } from '../objednavky/ui';
import { FAZE, POHLEDY, FazeOdznak, predDny, kDoreseniZakazniku } from './ui';

/**
 * Zákazníci → seznam · karta · třetí sloupec (norma §1, `panelVzdy`).
 *
 * Pohledy jsou fáze zákazníka (segmenty) počítané na serveru z objednávek
 * všech zdrojů. Panel ukazuje, kolik lidí je v které fázi, kolik objednávek
 * dnes poznáme podle člověka a „K dořešení" (ohrožení stálí zákazníci,
 * možné duplicity, záznamy bez kontaktu) — stejné položky jako mobilní řádek.
 */

const RAZENI = [
    { value: 'utrata', label: 'Útrata za 12 měsíců' },
    { value: 'pocet', label: 'Objednávek za 12 měsíců' },
    { value: 'posledni', label: 'Naposledy' },
    { value: 'utrata_celkem', label: 'Útrata celkem' },
    { value: 'jmeno', label: 'Jméno' },
];
const VYCHOZI_FILTR = { razeni: 'utrata' };

export default function ZakazniciSeznam({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, otevritId = null, onObjednavka, onRezervace, onKomunikace = null }) {
    const [radky, setRadky] = useState(null);
    const [meta, setMeta] = useState(null);
    const [pocty, setPocty] = useState({});
    const [prehled, setPrehled] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [vybranyId, setVybranyId] = useState(otevritId ? Number(otevritId) : null);
    const [hledani, setHledani] = useState('');
    const [hledaniServer, setHledaniServer] = useState('');
    const [pohled, setPohled] = useState('vsichni');
    const [filtr, setFiltr] = useState(VYCHOZI_FILTR);
    const [strana, setStrana] = useState(1);

    useEffect(() => { if (otevritId) setVybranyId(Number(otevritId)); }, [otevritId]);
    useEffect(() => {
        const t = setTimeout(() => setHledaniServer(hledani.trim()), 350);
        return () => clearTimeout(t);
    }, [hledani]);

    const nacti = useCallback(async (dalsi = 1) => {
        const p = new URLSearchParams({ pohled, sort: filtr.razeni, page: String(dalsi) });
        if (filtr.razeni === 'jmeno') p.set('dir', 'asc');
        if (hledaniServer) p.set('q', hledaniServer);
        try {
            const res = await fetch(`/api/zakaznici?${p}`, { headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(res.status === 403 ? 'Nemáte přístup.' : 'Zákazníky se nepodařilo načíst.');
            const d = await res.json();
            setChyba(null);
            setMeta(d.meta);
            setPocty(d.pocty || {});
            setStrana(dalsi);
            setRadky(prev => (dalsi > 1 && prev ? [...prev, ...d.data] : d.data));
        } catch (e) {
            setChyba(e.message);
            if (dalsi === 1) setRadky([]);
        }
    }, [pohled, filtr, hledaniServer]);

    const nactiPrehled = useCallback(() => {
        fetch('/api/zakaznici/prehled', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null)).then(d => d && setPrehled(d)).catch(() => {});
    }, []);

    useEffect(() => { setRadky(null); nacti(1); }, [nacti]);
    useEffect(() => { nactiPrehled(); }, [nactiPrehled]);

    const kDoreseni = useMemo(() => kDoreseniZakazniku(prehled), [prehled]);
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    const filtrSkupiny = useMemo(() => [
        { id: 'razeni', typ: 'dropdown', label: 'Řadit podle', options: RAZENI, vychozi: VYCHOZI_FILTR.razeni },
    ], []);
    const nastavFiltr = (id, hodnota) => setFiltr(f => ({ ...f, [id]: hodnota ?? VYCHOZI_FILTR[id] }));

    const grid = { display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 118px 74px 104px 104px 108px 132px', columnGap: 12, alignItems: 'center' };

    const radek = (z, siroky) => {
        const vybrany = z.id === vybranyId;
        const styl = {
            border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer', width: '100%', textAlign: 'left',
            background: vybrany ? SECTION_BG : 'transparent', boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
        };
        const jmeno = z.jmeno || 'Bez jména';
        const kanaly = [['R', z.kanaly.rozvoz], ['B', z.kanaly.bistro], ['V', z.kanaly.vydej]].filter(([, n]) => n > 0).map(([k, n]) => `${k} ${n}`).join(' · ');

        if (siroky) {
            return (
                <button key={z.id} type="button" onClick={() => setVybranyId(z.id)} style={{ ...styl, ...grid, padding: '0 14px', minHeight: 48 }}>
                    <span className="min-w-0">
                        <span className="flex items-center min-w-0" style={{ gap: 6 }}>
                            <span className="truncate" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{jmeno}</span>
                            <SouhlasSms sms={z.sms} />
                        </span>
                        <span className="truncate" style={{ display: 'block', fontSize: 11.5, color: muted }}>{[z.telefon, z.email].filter(Boolean).join(' · ') || 'bez kontaktu'}</span>
                    </span>
                    <span><FazeOdznak faze={z.faze} isDark={isDark} /></span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{z.pocet_12m || '—'}</span>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{z.utrata_12m ? fmtKc(z.utrata_12m) : '—'}</span>
                    <span style={{ fontSize: 12.5, color: muted, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{z.utrata ? fmtKc(z.utrata) : '—'}</span>
                    <span style={{ fontSize: 12.5, color: z.faze === 'ohrozeny' ? '#F87171' : 'var(--text-secondary)' }}>{predDny(z.dni_od_posledni)}</span>
                    <span className="truncate" style={{ fontSize: 12, color: muted }}>{kanaly || '—'}</span>
                </button>
            );
        }
        return (
            <button key={z.id} type="button" onClick={() => setVybranyId(z.id)}
                style={{ ...styl, display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 16px' : '9px 14px', minHeight: isMobile ? 60 : 54 }}>
                <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ margin: 0, fontSize: isMobile ? 15 : 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{jmeno} <SouhlasSms sms={z.sms} /></p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 12.5 : 11.5, color: z.faze === 'ohrozeny' ? '#F87171' : muted }}>
                        {FAZE[z.faze]?.label} · {z.pocet_12m} obj./rok · {predDny(z.dni_od_posledni)}
                    </p>
                </div>
                <span style={{ fontSize: isMobile ? 14.5 : 13.5, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {z.utrata_12m ? fmtKc(z.utrata_12m) : '—'}
                </span>
            </button>
        );
    };

    const otevriAkce = (p) => (p.zakaznikId ? (
        <button type="button" onClick={() => setVybranyId(p.zakaznikId)} className="inline-flex items-center"
            style={{ gap: 5, fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minHeight: 28 }}>
            Otevřít kartu <ExternalLink size={12} />
        </button>
    ) : null);

    const souhrn = () => {
        if (!prehled) return <p className="text-xs" style={{ color: muted }}>Načítám…</p>;
        const f = prehled.faze || {};
        const n = (k) => f[k]?.pocet || 0;
        const identifikovane = prehled.objednavky_30d ? Math.round((prehled.identifikovane_30d / prehled.objednavky_30d) * 100) : null;
        return (
            <>
                <Nadpis muted={muted}>Zákazníci podle fáze</Nadpis>
                {['fanousek', 'staly', 'vracejici', 'novacek', 'ohrozeny', 'jednorazovy', 'ztraceny'].map(k => (
                    <RadekPanelu key={k} popis={FAZE[k].label} hodnota={n(k).toLocaleString('cs-CZ')} cervene={k === 'ohrozeny'} muted={muted} cara={cara} />
                ))}
                <Nadpis muted={muted} style={{ marginTop: 16 }}>Posledních 30 dní</Nadpis>
                <RadekPanelu popis="Objednávky se známým zákazníkem" hodnota={identifikovane != null ? `${identifikovane} %` : '—'} muted={muted} cara={cara} />
                <RadekPanelu popis="Noví zákazníci tento měsíc" hodnota={prehled.novych_mesic} muted={muted} cara={cara} />
                <RadekPanelu popis="Celkem v evidenci" hodnota={prehled.celkem.toLocaleString('cs-CZ')} muted={muted} cara={cara} />
            </>
        );
    };

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column' }}>
            {souhrn()}
            <Nadpis muted={muted} style={{ marginTop: 16 }}>K dořešení</Nadpis>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} renderAkce={otevriAkce} prazdne="Nic." />
        </div>
    );

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat jméno, telefon, e-mail, adresu…', placeholderKratky: 'Hledat' }}
                filtr={(
                    <FiltrTlacitko skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={() => setFiltr(VYCHOZI_FILTR)}
                        pocetVysledku={meta?.celkem} siroky={siroky} kompaktni={isMobile} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} />
                )}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={() => setFiltr(VYCHOZI_FILTR)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} />
                )}
                pod={isMobile ? (
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} renderAkce={otevriAkce}
                        navic={<div style={{ padding: '14px 16px' }}>{souhrn()}</div>} />
                ) : null}
                pohledy={!siroky && !isMobile ? POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? CERVENA : undefined })) : null}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id] ?? null} aktivni={pohled === p.id}
                        upozorneni={p.upozorneni && (pocty[p.id] || 0) > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />

            {siroky && (radky?.length || 0) > 0 && (
                <div style={{ ...grid, padding: '9px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    <span>Zákazník</span><span>Fáze</span><span style={{ textAlign: 'right' }}>Obj. 12 m</span>
                    <span style={{ textAlign: 'right' }}>Útrata 12 m</span><span style={{ textAlign: 'right' }}>Celkem</span>
                    <span>Naposledy</span><span>Kanály</span>
                </div>
            )}

            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {chyba && <p className="text-sm text-center" style={{ color: '#F87171', padding: '24px 16px' }}>{chyba}</p>}
                {radky === null && !chyba && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {radky && !chyba && radky.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledaniServer ? 'Nic se neshoduje.' : pohled === 'ohrozeni' ? 'Nikdo z pravidelných zákazníků neodpadá.' : 'Nikdo tu není.'}
                    </p>
                )}
                {(radky || []).map(z => radek(z, siroky))}
                {/* Po přepnutí pohledu je seznam null, `meta` ještě ze starého. */}
                {radky && meta?.dalsi && (
                    <div style={{ padding: 14, display: 'flex', justifyContent: 'center' }}>
                        <button type="button" onClick={() => nacti(strana + 1)} className="rounded-lg"
                            style={{ minHeight: 40, padding: '0 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                            Načíst další ({radky.length} z {meta.celkem})
                        </button>
                    </div>
                )}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ZakaznikKarta key={vybranyId} id={vybranyId} isDark={isDark} isMobile={isMobile} mutedColor={muted}
                onZpet={() => setVybranyId(null)} onZakaznik={setVybranyId} onObjednavka={onObjednavka} onRezervace={onRezervace}
                onZmena={() => nacti(1)} onKomunikace={onKomunikace} rezervaDole={isMobile ? rezervaDole : null} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detail} panel={panel} />
        </div>
    );
}

/** Souhlas se SMS u zákazníka (2026-10-23): zelená = smí, červená přeškrtnutá = nechce, nic = neví se. */
function SouhlasSms({ sms }) {
    if (!sms) return null;
    const ano = sms === 'ano';
    return (
        <span title={ano ? 'Souhlasí se SMS' : 'Nechce SMS'} style={{ display: 'inline-flex', position: 'relative', flexShrink: 0, verticalAlign: '-1px', color: ano ? '#22C55E' : '#F87171' }}>
            <MessageSquare size={13} />
            {!ano && <span style={{ position: 'absolute', left: -1, right: -1, top: 6, height: 1.5, background: '#F87171', transform: 'rotate(-35deg)' }} />}
        </span>
    );
}
